import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import fr from './locales/fr.json'
import en from './locales/en.json'
import fon from './locales/fon.json'
import yo from './locales/yo.json'
import bba from './locales/bba.json'

/**
 * The translation layer, and the five languages the platform offers.
 *
 * <p>French is the *source* language, not a translation of one: every string in
 * the components is written in French, so `fr.json` holds the shared vocabulary
 * a translator starts from rather than the whole product. `fallbackLng: 'fr'`
 * therefore does the heavy lifting — a key with no translation in the chosen
 * language renders its French source, so a partly translated app reads as
 * French rather than as a screen full of dotted key names.
 *
 * <p>The bundles live in `locales/*.json` and not in this file on purpose:
 * filling in Fon or Bariba is a job for somebody who speaks it, and asking them
 * to edit TypeScript would be a reason for it never to happen.
 *
 * <p>What is actually translated today, honestly: `fr` is complete because it
 * is the source, `en` is a real translation of all of it, `yo` covers the
 * common vocabulary and navigation, and `fon` and `bba` carry little more than
 * the language names. The rest falls back to French rather than being invented
 * — a payment screen in words nobody checked is worse than one in French.
 */
export const SUPPORTED_LOCALES = ['fr', 'en', 'fon', 'yo', 'bba'] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

/**
 * What each language calls itself.
 *
 * <p>Endonyms, so the list is readable by the person looking for their own
 * language: somebody who reads only Yoruba cannot find "Yoruba" written in
 * French any more easily than in English.
 */
export const LOCALE_ENDONYM: Record<Locale, string> = {
  fr: 'Français',
  en: 'English',
  fon: 'Fɔngbè',
  yo: 'Yorùbá',
  bba: 'Baatɔnum',
}

const resources = {
  fr: { translation: fr },
  en: { translation: en },
  fon: { translation: fon },
  yo: { translation: yo },
  bba: { translation: bba },
}

/**
 * Roughly how much of the shared vocabulary each language actually has, as a
 * fraction of the French source. Drives the "partial translation" note on the
 * language screen — offering a language and silently showing French is the
 * thing that made the old screen feel broken.
 */
export function coverageOf(locale: Locale): number {
  const count = (bundle: object): number =>
    Object.values(bundle).reduce(
      (total: number, group) => total + Object.keys(group as object).length,
      0,
    )
  const source = count(fr)
  return source === 0 ? 1 : Math.min(1, count(resources[locale].translation) / source)
}

/** Remembered per device so the app opens in the right language before any request. */
const STORAGE_KEY = 'waago.locale'

export function storedLocale(): Locale {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return SUPPORTED_LOCALES.includes(value as Locale) ? (value as Locale) : 'fr'
  } catch {
    return 'fr'
  }
}

export function initI18n() {
  if (i18next.isInitialized) return i18next
  const lng = storedLocale()
  void i18next.use(initReactI18next).init({
    resources,
    lng,
    // A key with no translation renders its French source rather than the key
    // itself, so an app mid-extraction reads as French and not as broken.
    fallbackLng: 'fr',
    interpolation: { escapeValue: false },
  })
  // The document has to agree with the app, or a screen reader announces
  // French pronunciation over English text.
  document.documentElement.lang = lng
  return i18next
}

/**
 * Switches language now and remembers it.
 *
 * <p>Local first, then the account. The screen must change immediately whether
 * or not the request succeeds — a person who taps "English" on a bad connection
 * should not sit looking at French wondering if it worked.
 */
export function setLocale(locale: Locale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Private browsing. The change still applies for this session.
  }
  void i18next.changeLanguage(locale)
  document.documentElement.lang = locale
}
